import { useEffect } from "react";
import { ArrowUpRight, Mail, MessageCircle, Globe } from "lucide-react";
import * as Si from "react-icons/si";
import { useLanguage } from "../../contexts/LanguageContext";
import { Styles } from "../../styles/styles";
import {
  ActionIcon,
  Bio,
  Brand,
  BrandLogo,
  Footer,
  LinkCard,
  LinkIcon,
  LinkLabel,
  LinksColumn,
  LinksPage,
  LinkText,
  LanguageButton,
  LanguageSwitcher,
  SocialLinks,
  SocialLink,
} from "./styles";

const SiLinkedin = Si.SiLinkedin as unknown as React.FC<{ size?: number }>;
const SiInstagram = Si.SiInstagram as unknown as React.FC<{ size?: number }>;
const SiWhatsapp = Si.SiWhatsapp as unknown as React.FC<{ size?: number }>;

const Links = () => {
  const { language, setLanguage, t } = useLanguage();

  useEffect(() => {
    document.body.classList.add("links-page-body");

    return () => {
      document.body.classList.remove("links-page-body");
    };
  }, []);

  const links = [
    {
      ...t.links.items.company,
      href: "https://vizagetec.com",
      icon: <Globe>V</Globe>,
    },
    {
      ...t.links.items.whatsapp,
      href: "https://wa.me/5581998953665",
      icon: <MessageCircle size={21} strokeWidth={1.8} />,
    },
    {
      ...t.links.items.email,
      href: "mailto:contato@vizagetec.com",
      icon: <Mail size={21} strokeWidth={1.8} />,
    },
    {
      ...t.links.items.linkedin,
      href: "https://www.linkedin.com/company/vizagetec",
      icon: <SiLinkedin size={20} />,
    },
    {
      ...t.links.items.instagram,
      href: "https://www.instagram.com/vizagetec",
      icon: <SiInstagram size={20} />,
    },
  ];

  return (
    <>
      <Styles />
      <LinksPage>
        <Brand>
          <BrandLogo src="/img/svg/logo.png" alt="Vizage" />
        </Brand>

        <h1>{t.links.title}</h1>
        <Bio>{t.links.bio}</Bio>

        <LinksColumn>
          {links.map((link) => (
            <LinkCard
              key={link.label}
              href={link.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              <LinkIcon>{link.icon}</LinkIcon>
              <LinkText>
                <LinkLabel>{link.label}</LinkLabel>
                <span>{link.description}</span>
              </LinkText>
              <ActionIcon aria-hidden="true">
                <ArrowUpRight size={19} strokeWidth={1.8} />
              </ActionIcon>
            </LinkCard>
          ))}
        </LinksColumn>

        <SocialLinks>
          <SocialLink
            href="https://wa.me/5581998953665"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.links.socialLabels.whatsapp}
          >
            <SiWhatsapp size={18} />
          </SocialLink>
          <SocialLink
            href="mailto:contato@vizagetec.com"
            aria-label={t.links.socialLabels.email}
          >
            <Mail size={19} strokeWidth={1.8} />
          </SocialLink>
          <SocialLink
            href="https://www.linkedin.com/company/vizagetec"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.links.socialLabels.linkedin}
          >
            <SiLinkedin size={18} />
          </SocialLink>
          <SocialLink
            href="https://www.instagram.com/vizagetec"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t.links.socialLabels.instagram}
          >
            <SiInstagram size={18} />
          </SocialLink>
        </SocialLinks>

        <Footer>
          {t.links.footer}
          <LanguageSwitcher aria-label={t.common.language}>
            {(["pt-BR", "en", "es"] as const).map((option) => (
              <LanguageButton
                key={option}
                type="button"
                $active={language === option}
                onClick={() => setLanguage(option)}
              >
                {option === "pt-BR" ? "PT" : option.toUpperCase()}
              </LanguageButton>
            ))}
          </LanguageSwitcher>
        </Footer>
      </LinksPage>
    </>
  );
};

export default Links;